import { useEffect, useState, useRef, useCallback } from "react";
import { apiFetch } from "../../../config/api";
import { getUser } from "../../../config/auth";
import Pagination, { usePagination } from "../../../components/Pagination";
import Loader from "../../../components/Loader";
import Toast, { useToast } from "../../../components/Toast";
import EmptyState from "../../../components/EmptyState";
import PageHeader from "../../../components/PageHeader";

const PAGE_SIZE = 15;

export default function DepartmentsPage() {
  const me = getUser();
  const role = me?.role || "Staff";
  const canManage = role === "Admin" || role === "CXO";

  const { toast, showToast, dismissToast } = useToast();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [expandedDepts, setExpandedDepts] = useState({});

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formParentId, setFormParentId] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Delete confirm
  const [deletingId, setDeletingId] = useState(null);
  const pendingDeleteRef = useRef({});

  // Sub-team form
  const [showSubTeamForm, setShowSubTeamForm] = useState(null); // parent dept id
  const showSubTeamFormRef = useRef(null);
  const [subTeamName, setSubTeamName] = useState("");
  const [subTeamDesc, setSubTeamDesc] = useState("");
  const [savingSubTeam, setSavingSubTeam] = useState(false);
  const [subTeamError, setSubTeamError] = useState("");

  // Build hierarchy: top-level depts + their sub-teams
  const topLevelDepts = departments.filter((d) => !d.parentDepartmentId);
  const subDepts = departments.filter((d) => d.parentDepartmentId);

  // Auto-expand all parent depts when data loads so hierarchy is visible by default
  useEffect(() => {
    if (topLevelDepts.length > 0) {
      setExpandedDepts(prev => {
        const next = { ...prev };
        topLevelDepts.forEach(d => { if (!(d.id in next)) next[d.id] = true; });
        return next;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departments]);

  function getSubTeams(parentId) {
    return subDepts.filter((d) => d.parentDepartmentId === parentId);
  }

  const { totalPages, safePage, paginated } = usePagination(
    topLevelDepts,
    PAGE_SIZE,
  );
  const currentPage = safePage(page);
  const pagedDepts = paginated(currentPage);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/departments");
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setDepartments(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "Failed to load departments");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  // Keep ref in sync so the single persistent listener can check current value
  useEffect(() => { showSubTeamFormRef.current = showSubTeamForm; }, [showSubTeamForm]);

  // Single listener bound once — reads ref instead of closing over state
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape" && showSubTeamFormRef.current) setShowSubTeamForm(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function openCreate() {
    setEditingDept(null);
    setFormName("");
    setFormDesc("");
    setFormParentId("");
    setFormError("");
    setShowForm(true);
  }

  function openEdit(dept) {
    setEditingDept(dept);
    setFormName(dept.name);
    setFormDesc(dept.description || "");
    setFormParentId(dept.parentDepartmentId || "");
    setFormError("");
    setShowForm(true);
  }

  async function saveForm() {
    setFormError("");
    if (!formName.trim()) return setFormError("Department name is required");
    setSaving(true);
    try {
      const url = editingDept
        ? `/api/departments/${editingDept.id}`
        : "/api/departments";
      const method = editingDept ? "PUT" : "POST";
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify({
          name: formName.trim(),
          description: formDesc.trim() || null,
          parentDepartmentId: formParentId || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setShowForm(false);
      showToast(editingDept ? "Department updated" : "Department created");
      await load();
    } catch (e) {
      setFormError(e?.message || "Failed to save department");
    } finally {
      setSaving(false);
    }
  }

  const undoDelete = useCallback((id, snapshot) => {
    const pending = pendingDeleteRef.current[id];
    if (pending) {
      clearTimeout(pending.timer);
      delete pendingDeleteRef.current[id];
    }
    // Restore the dept (and its sub-teams) back into state sorted by name
    setDepartments(prev =>
      [...prev, ...snapshot].sort((a, b) => a.name.localeCompare(b.name))
    );
  }, []);

  function confirmDelete(id) {
    // Snapshot the dept + its sub-teams so undo can restore everything
    const snapshot = departments.filter(
      d => d.id === id || d.parentDepartmentId === id
    );
    setDeletingId(null);

    // Optimistically remove from UI immediately
    setDepartments(prev =>
      prev.filter(d => d.id !== id && d.parentDepartmentId !== id)
    );

    // Show toast with Undo button
    showToast("Department deactivated.", "success", {
      label: "Undo",
      onClick: () => undoDelete(id, snapshot),
    });

    // Schedule actual API call after 5 seconds
    const timer = setTimeout(async () => {
      delete pendingDeleteRef.current[id];
      try {
        const res = await apiFetch(`/api/departments/${id}`, { method: "DELETE" });
        if (!res.ok) {
          // Restore on API failure
          setDepartments(prev =>
            [...prev, ...snapshot].sort((a, b) => a.name.localeCompare(b.name))
          );
          setError(await res.text() || "Failed to deactivate department.");
        }
      } catch (e) {
        setDepartments(prev =>
          [...prev, ...snapshot].sort((a, b) => a.name.localeCompare(b.name))
        );
        setError(e?.message || "Failed to deactivate department.");
      }
    }, 5000);

    pendingDeleteRef.current[id] = { timer };
  }

  async function saveSubTeam(parentId) {
    if (!subTeamName.trim()) { setSubTeamError("Sub-team name is required."); return; }
    setSubTeamError("");
    setSavingSubTeam(true);
    try {
      const res = await apiFetch("/api/departments", {
        method: "POST",
        body: JSON.stringify({
          name: subTeamName.trim(),
          description: subTeamDesc.trim() || null,
          parentDepartmentId: parentId,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setShowSubTeamForm(null);
      setSubTeamName("");
      setSubTeamDesc("");
      setSubTeamError("");
      showToast("Sub-team created");
      // Auto-expand to show sub-teams
      setExpandedDepts((prev) => ({ ...prev, [parentId]: true }));
      await load();
    } catch (e) {
      setSubTeamError(e?.message || "Failed to create sub-team");
    } finally {
      setSavingSubTeam(false);
    }
  }

  return (
    <div>
      <Toast toast={toast} onDismiss={dismissToast} />
      {/* Header */}
      <PageHeader
        title="Departments"
        subtitle={`${topLevelDepts.length} department${topLevelDepts.length !== 1 ? "s" : ""} · ${subDepts.length} sub-team${subDepts.length !== 1 ? "s" : ""}`}
        actions={canManage && (
          <button className="btn" style={{ padding: "10px 18px", fontWeight: 600, borderRadius: 10 }} onClick={openCreate}>
            + New Department
          </button>
        )}
      />

      {error && (
        <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Hierarchical Table */}
      <div className="glass-card" style={{ overflow: "hidden" }}>
        {loading && (
          <div>
            <div className="tasks-row header" style={{ gridTemplateColumns: canManage ? "2fr 3fr 100px 180px" : "2fr 3fr 100px" }}>
              <div>Name</div><div>Description</div><div>Sub-teams</div>
              {canManage && <div>Actions</div>}
            </div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="tasks-row" style={{ gridTemplateColumns: canManage ? "2fr 3fr 100px 180px" : "2fr 3fr 100px", cursor: "default" }}>
                <Loader type="skeleton" height={14} width="55%" />
                <Loader type="skeleton" height={14} width="70%" />
                <Loader type="skeleton" height={14} width="25%" />
                {canManage && <Loader type="skeleton" height={26} width="80%" style={{ borderRadius: 8 }} />}
              </div>
            ))}
          </div>
        )}

        {!loading && topLevelDepts.length === 0 && (
          <EmptyState
            icon="🏢"
            title="No departments yet."
            subtitle={canManage ? 'Click "+ New Department" to add one.' : undefined}
          />
        )}

        {!loading && pagedDepts.length > 0 && (
          <div>
            {/* Header */}
            <div
              className="tasks-row header"
              style={{
                gridTemplateColumns: canManage
                  ? "2fr 3fr 100px 180px"
                  : "2fr 3fr 100px",
              }}
            >
              <div>Name</div>
              <div>Description</div>
              <div>Sub-teams</div>
              {canManage && <div>Actions</div>}
            </div>

            {pagedDepts.map((d) => {
              const subTeams = getSubTeams(d.id);
              const isExpanded = expandedDepts[d.id];

              return (
                <div key={d.id}>
                  {/* Parent row */}
                  <div
                    className="tasks-row"
                    style={{
                      gridTemplateColumns: canManage
                        ? "2fr 3fr 100px 180px"
                        : "2fr 3fr 100px",
                      cursor: "default",
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      {subTeams.length > 0 && (
                        <button
                          onClick={() =>
                            setExpandedDepts((prev) => ({
                              ...prev,
                              [d.id]: !prev[d.id],
                            }))
                          }
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            fontSize: 11,
                            color: "var(--text-3)",
                            padding: "2px 4px",
                            borderRadius: 4,
                          }}
                        >
                          {isExpanded ? "▼" : "▶"}
                        </button>
                      )}
                      {subTeams.length === 0 && <span style={{ width: 20 }} />}
                      <span className="task-title">{d.name}</span>
                    </div>
                    <div style={{ color: "var(--text-2)", fontSize: 13 }}>
                      {d.description || (
                        <span style={{ color: "var(--text-3)" }}>—</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                      {subTeams.length > 0 ? (
                        <button
                          onClick={() =>
                            setExpandedDepts((prev) => ({
                              ...prev,
                              [d.id]: !prev[d.id],
                            }))
                          }
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "var(--brand)",
                            fontSize: 12,
                            fontWeight: 600,
                            padding: 0,
                          }}
                        >
                          {subTeams.length} team
                          {subTeams.length !== 1 ? "s" : ""}
                        </button>
                      ) : (
                        "—"
                      )}
                    </div>
                    {canManage && (
                      <div
                        style={{ display: "flex", gap: 6, flexWrap: "wrap" }}
                      >
                        <button
                          className="btn-ghost"
                          style={{
                            padding: "4px 10px",
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                          onClick={() => openEdit(d)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-ghost"
                          style={{
                            padding: "4px 10px",
                            borderRadius: 8,
                            fontSize: 12,
                            color: "var(--brand)",
                          }}
                          onClick={() => {
                            setShowSubTeamForm(d.id);
                            setSubTeamName("");
                            setSubTeamDesc("");
                            setSubTeamError("");
                          }}
                        >
                          + Sub-team
                        </button>
                        <button
                          className="btn-danger"
                          style={{
                            padding: "4px 10px",
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                          onClick={() => setDeletingId(d.id)}
                        >
                          Deactivate
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Sub-team add form inline */}
                  {showSubTeamForm === d.id && (
                    <>
                    <div
                      style={{
                        background: "var(--surface-2)",
                        borderLeft: "3px solid var(--brand)",
                        padding: "12px 24px 12px 40px",
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          color: "var(--text-3)",
                          fontWeight: 600,
                        }}
                      >
                        New sub-team under {d.name}:
                      </span>
                      <input
                        value={subTeamName}
                        onChange={(e) => setSubTeamName(e.target.value)}
                        placeholder="Sub-team name *"
                        style={{
                          fontSize: 13,
                          padding: "5px 10px",
                          borderRadius: 8,
                          border: "1px solid var(--border-strong)",
                          width: 180,
                        }}
                      />
                      <input
                        value={subTeamDesc}
                        onChange={(e) => setSubTeamDesc(e.target.value)}
                        placeholder="Description (optional)"
                        style={{
                          fontSize: 13,
                          padding: "5px 10px",
                          borderRadius: 8,
                          border: "1px solid var(--border-strong)",
                          flex: 1,
                        }}
                      />
                      <button
                        onClick={() => saveSubTeam(d.id)}
                        disabled={savingSubTeam || !subTeamName.trim()}
                        style={{
                          padding: "5px 12px",
                          background: "var(--brand)",
                          color: "white",
                          border: "none",
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {savingSubTeam ? "Adding..." : "Add"}
                      </button>
                      <button
                        onClick={() => { setShowSubTeamForm(null); setSubTeamError(""); }}
                        style={{
                          padding: "5px 10px",
                          background: "none",
                          border: "1px solid var(--border-strong)",
                          borderRadius: 8,
                          fontSize: 12,
                          cursor: "pointer",
                          color: "var(--text-3)",
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    {subTeamError && (
                      <div style={{ padding: "6px 40px", fontSize: 12, color: "var(--danger-text)", background: "var(--danger-soft)", borderTop: "1px solid var(--danger-border)" }}>
                        {subTeamError}
                      </div>
                    )}
                    </>
                  )}

                  {/* Sub-teams (expanded) */}
                  {isExpanded &&
                    subTeams.map((sub) => (
                      <div
                        key={sub.id}
                        className="tasks-row"
                        style={{
                          gridTemplateColumns: canManage
                            ? "2fr 3fr 100px 180px"
                            : "2fr 3fr 100px",
                          background: "var(--surface-2)",
                          cursor: "default",
                        }}
                      >
                        <div
                          style={{
                            paddingLeft: 44,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span
                            style={{ color: "var(--text-3)", fontSize: 11 }}
                          >
                            └─
                          </span>
                          <span style={{ fontWeight: 500, fontSize: 13 }}>
                            {sub.name}
                          </span>
                        </div>
                        <div style={{ color: "var(--text-2)", fontSize: 12 }}>
                          {sub.description || (
                            <span style={{ color: "var(--text-3)" }}>—</span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                          Sub-team
                        </div>
                        {canManage && (
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              className="btn-ghost"
                              style={{
                                padding: "3px 9px",
                                borderRadius: 8,
                                fontSize: 11,
                              }}
                              onClick={() => openEdit(sub)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn-danger"
                              style={{
                                padding: "3px 9px",
                                borderRadius: 8,
                                fontSize: 11,
                              }}
                              onClick={() => setDeletingId(sub.id)}
                            >
                              Deactivate
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        )}

        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => setPage(safePage(p))}
          total={topLevelDepts.length}
          pageSize={PAGE_SIZE}
        />
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div
            className="glass-card"
            style={{
              width: 480,
              padding: 30,
              borderRadius: 18,
              boxShadow: "0 25px 60px rgba(0,0,0,0.14)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: 20,
                fontSize: 17,
                fontWeight: 700,
              }}
            >
              {editingDept ? "Edit Department" : "New Department"}
            </h3>

            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <label className="form-label">
                  Department Name <span style={{ color: "var(--danger)" }}>*</span>
                </label>
                <input
                  className="form-input"
                  placeholder="e.g., Finance, Operations"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  autoFocus
                />
              </div>
              <div>
                <label className="form-label">
                  Description{" "}
                  <span style={{ color: "var(--text-3)", fontWeight: 400 }}>
                    (optional)
                  </span>
                </label>
                <textarea
                  className="form-input"
                  rows={2}
                  placeholder="Brief description"
                  value={formDesc}
                  onChange={(e) => setFormDesc(e.target.value)}
                />
              </div>
              <div>
                <label className="form-label">
                  Parent Department{" "}
                  <span style={{ color: "var(--text-3)", fontWeight: 400 }}>
                    (for sub-teams)
                  </span>
                </label>
                <select
                  className="form-input"
                  value={formParentId}
                  onChange={(e) => setFormParentId(e.target.value)}
                >
                  <option value="">None (top-level department)</option>
                  {topLevelDepts
                    .filter((d) => d.id !== editingDept?.id)
                    .map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {formError && (
              <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
                {formError}
              </div>
            )}

            <div
              style={{
                marginTop: 24,
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <button
                className="btn-ghost"
                style={{ padding: "9px 16px", borderRadius: 10 }}
                onClick={() => setShowForm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn"
                style={{
                  padding: "9px 20px",
                  borderRadius: 10,
                  fontWeight: 600,
                }}
                onClick={saveForm}
                disabled={saving}
              >
                {saving ? "Saving..." : editingDept ? "Save Changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deletingId && (
        <div className="modal-overlay" onClick={() => setDeletingId(null)}>
          <div
            className="glass-card"
            style={{
              width: 400,
              padding: 28,
              borderRadius: 18,
              boxShadow: "0 25px 60px rgba(0,0,0,0.14)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: 10,
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              Deactivate Department?
            </h3>
            <p
              style={{
                color: "var(--text-2)",
                fontSize: 13,
                marginBottom: 22,
              }}
            >
              This will deactivate the department and any sub-teams under it.
              The data is not deleted — it will simply be hidden. Users assigned
              to it will not be affected.
            </p>
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}
            >
              <button
                className="btn-ghost"
                style={{ padding: "9px 16px", borderRadius: 10 }}
                onClick={() => setDeletingId(null)}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                style={{
                  padding: "9px 16px",
                  borderRadius: 10,
                  fontWeight: 600,
                }}
                onClick={() => confirmDelete(deletingId)}
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
